import { CustomerService, enumToExtendedArray, NumericFormatterValueConverter, PortfolioReportCache, property, widget, WidgetPropertyType } from '@dynamik/common';
import { combineLatestWith, debounceTime, delayWhen, ReplaySubject, switchMap, takeUntil, tap } from '../../../rxjs-local';
import { InstrumentValuesReportViewModel, ReportPageLayoutDeviceType } from '@dynamik/common/clients/reporting-clients';
import { ColumnState, GetContextMenuItemsParams, GridApi, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { BindingEngine } from 'aurelia-binding';
import { WidgetBase } from '@dynamik/common';
import { getExcelStyles } from '../../../resources/agGrid/excel-styles';
import { customElement } from 'aurelia-templating';
import { ThemeService } from '../../../services/theme-service';

declare module '@dynamik/common/clients/reporting-clients' {
  interface InstrumentValuesReportViewModel extends IInstrumentValuesReportViewModel {
    unrealisedPercent?: number;
  }
}

export enum PortfolioOverviewGroupBy {
  None = 1,
  Type = 2,
  Category = 3,
  TermAndType = 4,
  TermAndCategory = 5
}

@customElement('portfolio-overview-widget')
@widget('Portfolio overview', false, true, 'Description', [ReportPageLayoutDeviceType.Desktop])
export class PortfolioOverviewWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private portfolioReportCache: PortfolioReportCache,
    bindingEngine: BindingEngine, private numericFormatter: NumericFormatterValueConverter, public themeService: ThemeService) {
    super(element, bindingEngine, customerService);
  }

  gridApi: GridApi<InstrumentValuesReportViewModel>;
  suppressSaveColumnState: boolean;
  PortfolioOverviewGroupBy = PortfolioOverviewGroupBy;
  gridOptions: GridOptions<InstrumentValuesReportViewModel>;
  gridReady$ = new ReplaySubject<void>(1);
  quantityFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number, this.quantityPrecision);
  moneyFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number, this.precision);
  costPriceEaFormatter = (params: ValueFormatterParams<InstrumentValuesReportViewModel>) => params.data?.quantityClosingBalance
    ? this.numericFormatter.toView(params.data.costPriceClosingBalance / params.data.quantityClosingBalance, this.precision)
    : '';

  @property(null)
  columnStates?: ColumnState[];

  @property(null)
  filterModel?: ReturnType<GridApi['getFilterModel']>;

  @property('Data', undefined, WidgetPropertyType.List, enumToExtendedArray(PortfolioOverviewGroupBy))
  groupBy: PortfolioOverviewGroupBy = PortfolioOverviewGroupBy.None;

  @property('Data', 'Category type', WidgetPropertyType.List)
  categoryTypeId?: number;
  async categoryTypeIdListValues() {
    return this.customerService.instrumentCategories.filter(x => x.enabled).map(x => ({ value: x.catTypeId, label: x.catTypeName }));
  }

  @property('Data')
  groupsClosed: boolean;

  @property('Data')
  hidePaging: boolean;

  @property('Data')
  quantityPrecision?: number = 0;

  @property('Data')
  precision: number = 2;

  @property('Table')
  fontSize?: number = 8;

  @property('Table')
  rowHeight?: number = 20;

  override subscribeToFilter() {
    this.customerService.customerAndDates$
      .pipe(
        delayWhen(() => this.gridReady$),
        combineLatestWith(this.refresh$),
        debounceTime(500),
        tap(() => {
          this.error = undefined;
          this.gridApi.setGridOption('loading', true);
          this.notifyLoading();
        }),
        switchMap(async ([filter]) => this.portfolioReportCache
          .instrumentValues(filter.customer.companyId, filter.dates.fromStr, filter.dates.toStr, this.groupBy === PortfolioOverviewGroupBy.Category ? this.categoryTypeId : undefined, undefined, false)
          .then(x => x.filter(y => y.quantityClosingBalance !== 0))
          .catch(e => e as Error)),
        takeUntil(this.destroy$)
      )
      .subscribe(async x => {
        this.gridApi.setGridOption('loading', false);
        if (x instanceof Error) {
          this.error = x;
        } else {
          x.forEach(y => {
            y.unrealisedPercent = y.unrealizedProfitLossClosingBalance / y.costPriceClosingBalance * 100;
          });
          this.gridApi.setGridOption('rowData', x);
          this.gridApi.setGridOption('pinnedBottomRowData', [this.getTotal(x)]);
          this.excelData = {
            fileName: 'Portfolio Overview',
            data: { Items: x }
          };
        }
        // give the grid time to render groups, otherwise the number of pages is wrong
        await Promise.delay(100);
        this.notifyLoaded();
      });
  }

  getTotal(x: InstrumentValuesReportViewModel[]) {
    return x.reduce((p, c) => {
      p.instrumentId = -1;
      p.estimatedValueClosingBalance += c.estimatedValueClosingBalance;
      p.estimatedValuePercent += c.estimatedValuePercent;
      p.costPriceClosingBalance += c.costPriceClosingBalance;
      p.unrealizedProfitLossClosingBalance += c.unrealizedProfitLossClosingBalance;
      p.unrealizedProfitLossEndOfLastYear += c.unrealizedProfitLossEndOfLastYear;
      p.unrealizedProfitLossChangeYearToDate += c.unrealizedProfitLossChangeYearToDate;
      p.realizedProfitLossChangeYearToDate += c.realizedProfitLossChangeYearToDate;
      p.dividendChangeYearToDate += c.dividendChangeYearToDate;
      p.interestChangeYearToDate += c.interestChangeYearToDate;
      p.profitLossChangeYearToDate += c.profitLossChangeYearToDate;
      return p;
    },
      new InstrumentValuesReportViewModel({
        name: 'TOTAL', estimatedValueClosingBalance: 0, costPriceClosingBalance: 0, unrealizedProfitLossEndOfLastYear: 0,
        unrealizedProfitLossClosingBalance: 0, unrealizedProfitLossChangeYearToDate: 0, realizedProfitLossChangeYearToDate: 0,
        dividendChangeYearToDate: 0, interestChangeYearToDate: 0, profitLossChangeYearToDate: 0, estimatedValueChangeYearToDate: 0
      }));
  }

  override activate(config: unknown) {
    super.activate(config)
    this.gridOptions = {
      defaultColDef: {
        filter: 'agTextColumnFilter',
        sortable: true,
        resizable: true
      },
      autoGroupColumnDef: {
        valueGetter: params => params.data.name,
        cellRendererParams: {
          totalValueGetter: params => {
            const isRootLevel = params.node.level === -1;
            if (isRootLevel) {
              return 'Total';
            }
            return `Total ${typeof (params.value) === 'boolean'
              ? params.value ? 'Long term' : 'Short term'
              : params.value}`;
          },
        }
      },
      getContextMenuItems: params => this.getContextMenuItems(params),
      popupParent: document.body,
      rowHeight: this.rowHeight,
      excelStyles: getExcelStyles(9),
      rowModelType: 'clientSide',
      rowSelection: { mode: 'singleRow', checkboxes: false },
      paginationAutoPageSize: true,
      pagination: true,
      paginateChildRows: true,
      groupDefaultExpanded: this.groupsClosed ? 0 : -1,
      groupTotalRow: 'bottom',
      suppressAggFuncInHeader: true,
      groupHideOpenParents: false,
      animateRows: false,
      debug: false,
      scrollbarWidth: 11,
      onColumnMoved: () => this.saveColumnState(),
      onColumnVisible: () => this.saveColumnState(),
      onColumnResized: () => this.saveColumnState(),
      onFilterChanged: () => this.saveColumnState(),
      onSortChanged: () => this.saveColumnState(),
      onDisplayedColumnsChanged: params => {
        if (params.source !== 'gridInitializing') {
          this.saveColumnState();
        }
      },
      getRowId: x => x.data.instrumentId.toString(),
      onGridReady: params => {
        this.gridApi = params.api;
        this.gridReady$.next();
      },
      onFirstDataRendered: () => {
        this.suppressSaveColumnState = true;
        setTimeout(() => this.suppressSaveColumnState = false, 2000);
        if (this.columnStates?.length) {
          this.gridApi.applyColumnState({ state: this.columnStates, applyOrder: true });
        }
        if (this.filterModel?.length) {
          this.gridApi.setFilterModel(this.filterModel);
        }
      }
    };
  }

  getPageCount(): number {
    return this.gridApi.paginationGetTotalPages();
  }

  async setPage(page: number): Promise<void> {
    if (page === this.gridApi.paginationGetCurrentPage()) {
      return;
    }
    this.gridApi.paginationGoToPage(page);
    // give the grid time to render
    await Promise.delay(100);
  }

  override configChanged(key: keyof PortfolioOverviewWidget) {
    switch (key) {
      case 'groupBy':
        this.groupByChanged();
        break;
      case 'categoryTypeId':
        this.categoryTypeIdChanged();
        break;
      case 'groupsClosed':
        this.groupClosedChanged();
        break;
      case 'rowHeight':
        this.rowHeightChanged();
        break;
      case 'quantityPrecision':
      case 'precision':
        this.refresh$.next();
        break;
      default:
        break;
    }
  }

  saveColumnState() {
    if (this.suppressSaveColumnState) {
      return;
    }
    this.columnStates = this.gridApi.getColumnState();
    this.filterModel = this.gridApi.getFilterModel();
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  groupByChanged() {
    const termCol = this.gridApi.getColumn('longTerm');
    const typeCol = this.gridApi.getColumn('type');
    const catCol = this.gridApi.getColumn('category');
    this.gridApi.removeRowGroupColumns([catCol, termCol, typeCol]);
    this.gridApi.setColumnsVisible([typeCol], true);
    this.gridApi.setColumnsVisible([termCol, catCol], false);
    this.categoryTypeId = undefined;
    switch (this.groupBy) {
      case PortfolioOverviewGroupBy.TermAndType:
        this.gridApi.addRowGroupColumns(['longTerm', 'type']);
        this.gridApi.setColumnsVisible([termCol, typeCol], false);
        break;
      case PortfolioOverviewGroupBy.TermAndCategory:
        this.gridApi.addRowGroupColumns(['longTerm', 'category']);
        this.gridApi.setColumnsVisible([termCol, typeCol], false);
        break;
      case PortfolioOverviewGroupBy.Type:
        this.gridApi.addRowGroupColumns(['type']);
        this.gridApi.setColumnsVisible([typeCol], false);
        break;
      case PortfolioOverviewGroupBy.Category:
        this.gridApi.addRowGroupColumns(['category']);
        this.gridApi.setColumnsVisible([catCol], false);
        break;
    }
    this.columnStates = this.gridApi.getColumnState();
    this.refresh$.next();
  }

  categoryTypeIdChanged() {
    this.refresh$.next();
  }

  groupClosedChanged() {
    if (this.groupsClosed) {
      this.gridApi.collapseAll();
    } else {
      this.gridApi.expandAll();
    }
  }

  rowHeightChanged() {
    this.gridApi.setGridOption('rowHeight', this.rowHeight);
    this.gridApi.onRowHeightChanged();
  }

  getContextMenuItems(params: GetContextMenuItemsParams<InstrumentValuesReportViewModel>) {
    return [
      'autoSizeAll',
      'resetColumns',
      { name: 'Fit All Columns', action: () => this.gridApi.sizeColumnsToFit() },
      'separator',
      {
        name: 'Export to Csv',
        action: async () => params.api.exportDataAsCsv({ columnSeparator: ';' }),
      },
      {
        name: 'Export to Excel',
        action: async () => params.api.exportDataAsExcel({

          skipPinnedTop: false,
          skipPinnedBottom: false,
          allColumns: false,
          onlySelected: false,
          fileName: 'PortfolioOverview',
          sheetName: 'PortfolioOverview'
        }),
      }
    ];
  }

  termValueFormatter = (params: ValueFormatterParams<InstrumentValuesReportViewModel, boolean>) => params.value ? 'Long term' : 'Short term';
}
